import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import BookmarkSection from "./BookmarkSection";
import {
  FaCarrot, FaBook, FaLock, FaCompass, FaCalendarAlt, FaBookmark,
  FaChevronLeft, FaChevronRight,
} from "react-icons/fa";

const Sidebar = () => {
  const { currentUser } = useAuth();
  const [hoverOpen, setHoverOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isRecipesPage = location.pathname === "/recipes";
  const isPantryPage = location.pathname === "/pantry";
  const currentView = isRecipesPage ? (searchParams.get("view") || "public") : searchParams.get("view");
  const currentCollection = searchParams.get("collection") || "";
  const currentPantryTab = isPantryPage ? (searchParams.get("tab") || "pantry") : searchParams.get("tab");

  const isOpen = pinned || hoverOpen;

  // Sync pin state from backend on mount
  useEffect(() => {
    if (currentUser?.sidebar_pinned) {
      setPinned(true);
    }
  }, [currentUser?.sidebar_pinned]);

  const togglePin = async () => {
    const newPinned = !pinned;
    setPinned(newPinned);
    try {
      await axiosInstance.patch("/api/user/profile/", { sidebar_pinned: newPinned });
    } catch (err) {
      console.error("Failed to save sidebar pin:", err);
      setPinned(!newPinned);
    }
  };

  const isActive = (path, view) => {
    if (path === "/pantry") {
      const targetTab = view || "pantry";
      return isPantryPage && currentPantryTab === targetTab;
    }
    if (view) return currentView === view;
    return location.pathname === path;
  };

  const navItems = [
    { icon: FaCarrot, label: "My Pantry", path: "/pantry", view: "pantry", section: "Pantry" },
    { icon: FaBook, label: "Shopping", path: "/pantry", view: "shopping" },
    { icon: FaCompass, label: "Recipe Ideas", path: "/pantry", view: "suggested" },
    { icon: FaBook, label: "Public", view: "public", section: "My Recipes" },
    { icon: FaLock, label: "Private", view: "private" },
    { icon: FaCompass, label: "Discover", view: "discover", section: "Explore" },
    { icon: FaCalendarAlt, label: "Planner", path: "/meal-planner", section: "Plan" },
    { icon: FaBookmark, label: "Saved", section: "Saved", isBookmark: true },
  ];

  const handleNav = (item) => {
    if (item.path === "/pantry") {
      navigate(`/pantry?tab=${item.view || "pantry"}`);
    } else if (item.view) {
      navigate(`/recipes?view=${item.view}`);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const activeClass = "text-blue-600 bg-blue-50 dark:bg-blue-900/30";
  const inactiveClass = "text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-300";

  return (
    <div
      onMouseEnter={() => !pinned && setHoverOpen(true)}
      onMouseLeave={() => !pinned && setHoverOpen(false)}
      className={`bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col ${
        isOpen ? "w-36" : "w-14"
      } min-h-screen overflow-hidden shrink-0`}
    >
      {/* Pin toggle — visible when expanded */}
      <div className={`flex items-center px-3 pt-4 pb-2 ${isOpen ? "justify-end" : "justify-center"}`}>
        {isOpen ? (
          <button
            onClick={togglePin}
            className="flex items-center transition"
          >
            <div className={`relative w-7 h-4 rounded-full transition-colors ${
              pinned ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
            }`}>
              <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${
                pinned ? "translate-x-3.5" : "translate-x-0.5"
              }`} />
            </div>
          </button>
        ) : (
          <div className="h-6" />
        )}
      </div>

      {/* Navigation items */}
      <nav className="flex-1 px-2 space-y-1">
        {navItems.map((item, i) => {
          const active = !item.isBookmark && isActive(item.path, item.view);

          // Section header — only when expanded
          const showSection = item.section && isOpen;
          const sectionHeader = showSection && (
            <p key={`section-${i}`} className={`type-eyebrow ${i > 0 ? "mt-4" : ""} mb-1 px-2`}>
              {item.section}
            </p>
          );

          // Spacer for collapsed section gaps
          const sectionSpacer = item.section && !isOpen && i > 0 && (
            <div key={`spacer-${i}`} className="h-2" />
          );

          // Bookmark section — special rendering
          if (item.isBookmark) {
            return (
              <div key="bookmarks">
                {sectionSpacer}
                {sectionHeader}
                {isOpen ? (
                  <BookmarkSection currentView={currentView} currentCollection={currentCollection} />
                ) : (
                  <button
                    onClick={() => navigate("/recipes?view=bookmarks")}
                    className={`w-full flex items-center justify-center p-2.5 rounded transition ${
                      currentView === "bookmarks" ? activeClass : inactiveClass
                    }`}
                    title="Bookmarks"
                  >
                    <FaBookmark size={16} />
                  </button>
                )}
              </div>
            );
          }

          return (
            <div key={item.label}>
              {sectionSpacer}
              {sectionHeader}
              <button
                onClick={() => handleNav(item)}
                className={`w-full flex items-center gap-3 rounded transition ${
                  active ? activeClass + " font-semibold" : inactiveClass
                } ${isOpen ? "px-2 py-1.5 text-sm" : "justify-center p-2.5"}`}
                title={!isOpen ? item.label : undefined}
              >
                <item.icon size={16} className="shrink-0" />
                {isOpen && <span className="type-button">{item.label}</span>}
              </button>
            </div>
          );
        })}
      </nav>

      {/* Collapse/expand toggle at bottom */}
      <div className="p-3 flex justify-center">
        <button
          onClick={() => {
            if (pinned) {
              togglePin();
            } else {
              setHoverOpen(!hoverOpen);
            }
          }}
          className="p-2 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          title={isOpen ? "Collapse" : "Expand"}
        >
          {isOpen ? <FaChevronLeft size={14} /> : <FaChevronRight size={14} />}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
